/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { CONVERT_OPTIONS_FACADE_TOKEN, IConvertOptionsFacade } from '../../../../../../data-access/src/lib/domain/facades-models/convert-options-facade-interface';
import { Component, EventEmitter, Input, Output, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { ConvertSystemInfo } from '../../../models/convert-system-info';
import { ConvertOptionReturnCodes } from '../../../models/place-coupling-return-codes';
import { GlobalConstants } from '../../../constants/global-constants';
import { SysqueElectricalState } from '../../../models/sysque-electrical-state';
import { CheckBoxConfig } from '@hcworkspace/sysque/shared/ui';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { ConvertOptionState } from '../../../models/convert-option-state';
import { ActivatedRoute } from '@angular/router';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';
import { take } from 'rxjs';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-convert-options',
  templateUrl: './convert-options.component.html',
  styleUrls: ['./convert-options.component.scss'],
  standalone: false
})
export class ConvertOptionsComponent implements OnInit {
  @Input() height: number;
  @Output() close = new EventEmitter();
  public componentTitle = '';
  public isRetain = false;
  public isIgnore = false;
  public alertStatus: AlertStatus = AlertStatus.Warning;
  public isAlertVisible = false;
  public showClose = false;
  public buttonContent = '';
  public systemName = '';
  public categoryName = '';
  public convertSystemInfo = new ConvertSystemInfo();
  public dockedState = new SysqueElectricalState();
  public convertOptionState = new ConvertOptionState();
  public componentAlertTitle = '';
  public msgcontent = '';
  public warningMessage: string;
  public itemList: string[] = [
    GlobalConstants.ServiceTypeNameAndColor,
    GlobalConstants.ConduitIdNumberAndFilter,
    GlobalConstants.ValuesOfSharedProjectParameters,
  ];
  public showAlignmentOptions = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: this.showClose,
    };
  }

  //#region HTML properties

  public convertSystemBody = GlobalConstants.ConvertSystemBody;
  public retainExistingObjectData = GlobalConstants.RetainTheExistingObjectData;
  public ignoreMessage = GlobalConstants.IgnoreMessageForSysQueSession;
  public alertName = GlobalConstants.AlertNameConvertSystem;
  public selectCouplingAlignmentLabel = GlobalConstants.SelectCouplingAlignment;

  public alignmentOptions = [ 
    { text: GlobalConstants.AlignedCouplingButton, icon: 'assets/img/aligned-coupling.png' }, 
    { text: GlobalConstants.OptimizedCouplingButton, icon: 'assets/img/optimized-coupling.png' } 
  ];
  public selectedAlignment = '';
  public shouldHideAcrylicSurface: boolean;

  //#endregion

  private alignmentOptionHeight = 678;
  private noAlignmentOptionHeight = 518; 
  private noConduitOrFieldFabShapeHeight = this.noAlignmentOptionHeight - 35; 

  constructor(
    @Inject(CONVERT_OPTIONS_FACADE_TOKEN) public facade: IConvertOptionsFacade,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {
    let dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    let releaseVersion = dockedState.releaseVersion;
    this.shouldHideAcrylicSurface = this.isVersionLessThan121(releaseVersion);
  }

  //#region Public methods

  /**
   * Initialize the component
   */
  public ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.componentTitle = data.componentTitle;
    });
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));

    // Register the controllers
    this.facade.registerControllers();
    
    this.getButtonContent();
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.systemName = this.dockedState?.selectedSystem ?? '';
    this.categoryName = this.dockedState?.categoryList[0].tabName ?? '';
    this.setWindowWithOrWithoutAlignmentOptions();
    this.validateSelection();
    this.showAlert();
  }

  /**
   * Destroy the component
   */
  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
  }

  /**
   * Handle the storage event
   */
  public handleStorageEvent(event: StorageEvent): void {
    const isMatchedKey =
      event.key === GlobalConstants.PLACE_COUPLING_RESPONSE || event.key === GlobalConstants.CONVERT_SYSTEM_RESPONSE;
    const newEventValue = JSON.parse(event.newValue) === '';
    if (!isMatchedKey || newEventValue) {
      return;
    }
    this.showAlertForConvertSystem(event);
    this.componentAlertTitle = this.facade.getLocalStorage(GlobalConstants.CONVERT_OPTION_TITLE);
    this.facade.updateLocalStorage(GlobalConstants.MSG_TITLE, this.componentAlertTitle);
    this.facade.updateLocalStorage(GlobalConstants.MSG_CONTENT, this.msgcontent);
  }

  /**
   * Show alert for convert system
   * @param event - storage event
   */
  public showAlertForConvertSystem(event: StorageEvent): void {
    const response: ConvertOptionReturnCodes = JSON.parse(event.newValue);
    switch (response) {
      case ConvertOptionReturnCodes.Success:
        this.componentAlertTitle = '';
        this.msgcontent = '';
        break;
      case ConvertOptionReturnCodes.NoFittingSupport:
        this.msgcontent = GlobalConstants.FittingsDoNotSupportConvertSystem;
        break;
      case ConvertOptionReturnCodes.NoSelection:
        this.msgcontent = GlobalConstants.SelectAtLeastOneFabConduit;
        break;
      case ConvertOptionReturnCodes.ConduitTypeRequired:
        this.componentAlertTitle = GlobalConstants.FabConduitTypeRequired;
        this.msgcontent = GlobalConstants.FabConduitTypeNotAvailable;
        break;
      case ConvertOptionReturnCodes.NoSelectionCt:
        this.msgcontent = GlobalConstants.SelectAtLeastOneCableTray;
        break;
      case ConvertOptionReturnCodes.CableTrayTypeRequired:
        this.componentAlertTitle = GlobalConstants.CableTrayTypeRequired;
        this.msgcontent = GlobalConstants.CableTrayTypeNotAvailable;
        break;
      case ConvertOptionReturnCodes.CableTraySizeMismatch:
        this.componentAlertTitle = GlobalConstants.ConvertSystem;
        this.msgcontent = GlobalConstants.CableTraySizeNotFound;
        break;
      default:
        break;
    }
  }

  /**
   * Save the data
   */
  public saveData(): void {
    this.convertSystemInfo = new ConvertSystemInfo();
    this.convertSystemInfo.systemName = this.systemName;
    this.convertSystemInfo.isIgnoreChecked = this.isIgnore;
    this.convertSystemInfo.isRetainChecked = this.isRetain;
    this.convertSystemInfo.isCouplingAlignment = this.selectedAlignment === GlobalConstants.AlignedCouplingButton;
    this.facade.saveData(this.convertSystemInfo);
    this.closeWindow();
  }

  /**
   * Show the alert
   */
  public showAlert(): void {
    this.warningMessage = '<span>' + GlobalConstants.WarningToAvoidLossOfCustomData + '</span>';
    this.alertStatus = AlertStatus.Warning;
    this.isAlertVisible = true;
    this.showClose = false;
  }

  /**
   * Get button content
   */
  public getButtonContent(): void {
    this.buttonContent =
      this.componentTitle === GlobalConstants.ConvertSystem ? GlobalConstants.Convert : GlobalConstants.Place;
  }

  /**
   * Check the value of retain check box
   * @param event - the event value
   */
  public onRetainValueChanged(event: any): void {
    this.isRetain = event.detail;
    this.facade.updateRetainValue(this.isRetain);
  }

  /**
   * Check the value of ignore check box
   * @param event - the event value
   */
  public onIgonreValueChanged(event: any): void {
    this.isIgnore = event.detail;
    this.facade.updateRetainValue(this.isRetain);
  }

  public alignmentChange(event: any): void {
    this.selectedAlignment = event.value;
    this.facade.updateCouplingAlignment(this.selectedAlignment === GlobalConstants.AlignedCouplingButton);
  }

  /**
   * Update the response
   * @param response - the response value
   */
  public updateResponse(response): void {
    switch (response) {
      case ConvertOptionReturnCodes.NoSelection:
        this.componentAlertTitle = this.facade.getLocalStorage(GlobalConstants.CONVERT_OPTION_TITLE);
        this.facade.updateLocalStorage(GlobalConstants.MSG_TITLE, this.componentAlertTitle);
        this.facade.updateLocalStorage(
          GlobalConstants.MSG_CONTENT,
          GlobalConstants.PleaseSelectAtLeastOneSystem
        );
        break;
      case ConvertOptionReturnCodes.IsWireMeshAndClearance:
        this.componentAlertTitle = this.facade.getLocalStorage(GlobalConstants.CONVERT_OPTION_TITLE);
        this.facade.updateLocalStorage(GlobalConstants.MSG_TITLE, this.componentAlertTitle);
        this.facade.updateLocalStorage(
          GlobalConstants.MSG_CONTENT,
          GlobalConstants.SelectedStructureDoesNotSupport
        );
        break;
      default:
        this.initializeData();
    }
  }

  /**
   * Initialize the data
   */
  public initializeData(): void {
    this.facade.getInitialData().pipe(take(1)).subscribe((response) => {
      if (response != null) {
        this.convertSystemInfo.systemName = response.systemName;
        this.convertSystemInfo.isIgnoreChecked = response.isIgnoreChecked;
        this.convertSystemInfo.isRetainChecked = response.isRetainChecked;
        this.convertSystemInfo.showLabel = response.showLabel;
        this.convertSystemInfo.isCouplingAlignment = response.isCouplingAlignment;
        this.selectedAlignment = this.convertSystemInfo.isCouplingAlignment ? GlobalConstants.AlignedCouplingButton : GlobalConstants.OptimizedCouplingButton;
        this.isRetain = this.convertSystemInfo.isRetainChecked;
        this.isIgnore = this.convertSystemInfo.isIgnoreChecked;
        if(this.isIgnore){
          this.saveData();
        }
      }
      if (!this.convertSystemInfo.showLabel) {
        this.itemList = this.itemList.filter((x) => x != GlobalConstants.ConduitIdNumberAndFilter);
        this.facade.changeHeight(this.noConduitOrFieldFabShapeHeight);
      }
    });
  }

  /**
   * Get dockable variables
   */
  public getDockableVariables(): void {
    this.convertOptionState.selectedSystem = this.dockedState?.selectedSystem;
    this.convertOptionState.cableTrayNumberText = this.dockedState?.cableTrayNumberText as SysqueNumberBoxVM[];
    this.convertOptionState.checkBoxConfigs = this.dockedState?.checkBoxConfigs as CheckBoxConfig[];
    this.convertOptionState.isCableTrayClearanceNeeded = this.dockedState?.isCableTrayClearanceNeeded;
    this.convertOptionState.isHangerNeeded = this.dockedState?.isHangerNeeded;
    this.convertOptionState.onlyFittingProcess = this.dockedState?.onlyFittingProcess;
    this.convertOptionState.projectAndPanelName = this.dockedState?.projectAndPanelName as string[];
    this.convertOptionState.projectName = this.dockedState?.projectName;
    this.convertOptionState.wirefillSpec = this.dockedState?.wirefillSpec;
    this.convertOptionState.selectedCategory = this.dockedState?.selectedCategory;
  }

  /**
   * Close the window
   */
  public closeWindow(): void {    
    if(this.shouldHideAcrylicSurface){
      this.acrylicSurfaceService.hide();
    }
    this.facade.closeWindow();
    this.ngOnDestroy();
    this.close.emit();
  }

  //#endregion

  //#region Private methods

  /**
   * Validate the selection
   */
  private validateSelection(): void {
    this.getDockableVariables();
    this.facade.updateLocalStorage(GlobalConstants.CONVERT_SYSTEM_RESPONSE, '');
    this.facade.updateLocalStorage(GlobalConstants.PLACE_COUPLING_RESPONSE, '');
    this.facade.updateLocalStorage(GlobalConstants.MSG_TITLE, '');
    this.facade.updateLocalStorage(GlobalConstants.MSG_CONTENT, '');
    this.facade.validateSelection(this.convertOptionState).pipe(take(1)).subscribe((response) => {
      if (response != null) {
        this.updateResponse(response);
      }
    });
  }

  /**
   * Set window height based on category name
   */
  private setWindowWithOrWithoutAlignmentOptions(): void {
    if (this.categoryName === GlobalConstants.FieldFabConduit) {
      this.showAlignmentOptions = true;
      this.facade.changeHeight(this.alignmentOptionHeight);
    } else {
      this.showAlignmentOptions = false;
      this.facade.changeHeight(this.noAlignmentOptionHeight);
    }
    this.cd.detectChanges();
  }
  
  /**
   * Checks if the given version is less than 12.1.0.
   * Handles version strings with 2 or 3 segments (e.g., "12.1", "12.1.1").
   * @param version The version string to check (e.g., "12.1", "12.1.1", "12.0.0").
   * @param target The target version string to compare against (default: "12.1.0").
   * @returns True if version < target, false otherwise.
   */
  private isVersionLessThan121(version: string, target: string = '12.1.0'): boolean {
    const parse = (v: string) => v.split('.').map(Number);
    const [major = 0, minor = 0, patch = 0] = parse(version).concat([0, 0]).slice(0, 3);
    const [targetMajor, targetMinor, targetPatch] = parse(target).concat([0, 0]).slice(0, 3);

    if (major < targetMajor) {
      return true;
    }
    if (major > targetMajor) {
      return false;
    }
    if (minor < targetMinor) {
      return true;
    }
    if (minor > targetMinor) {
      return false;
    }

    return patch < targetPatch;
  }

  //#endregion
}
